import { useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { flowAsText, redactor, type FlowCell, type Round } from '@flow/core';
import { run, useDeps, useUseCases } from '../../app/services';
import { ApiError, apiFetch } from '../../infra/api';
import { useAccount } from '../account/AccountProvider';
import { createElement } from 'react';
import { Copy } from '../../ui/Brand';

/** AI works only for a signed-in, invited judge who is online. Everything AI is optional; the app never waits on it. */
export const useAiAvailable = () => {
  const { state } = useAccount();
  return state.status === 'signedIn' && !!state.user && !state.problem && navigator.onLine;
};

const message = (e: unknown) =>
  e instanceof ApiError && e.body && typeof e.body === 'object' && 'message' in e.body ? String(e.body.message) : "Couldn't reach {flow}'s AI service.";

/** Suggest a title for one argument. Text is redacted here, on the device; team codes are put back in the answer. */
export const useSuggestTitle = () => {
  const { getToken } = useAccount();
  const deps = useDeps();
  return useCallback(async (round: Round, text: string) => {
    const { redact, restore } = redactor(round, await deps.names.get(round.id));
    const r = await apiFetch<{ title: string }>('/api/ai/title', getToken, { method: 'POST', body: JSON.stringify({ text: redact(text), event: round.format.event }) });
    return restore(r.title).trim();
  }, [deps, getToken]);
};

/** Write the post-round recap. Only called for submitted rounds (the use case refuses otherwise). */
export const useSummarizeRound = () => {
  const { getToken } = useAccount();
  const deps = useDeps();
  const uc = useUseCases();
  return useCallback(async (roundId: string, opts: { quiet?: boolean } = {}) => {
    const round = await deps.rounds.get(roundId);
    if (!round || round.status !== 'submitted') return;
    const [rows, cells, names, ballot] = await Promise.all([deps.flow.rows(roundId), deps.flow.cells(roundId), deps.names.get(roundId), deps.ballots.get(roundId)]);
    const flow = flowAsText(round, rows, new Map(cells.map((c): [string, FlowCell] => [c.id, c])), names, ballot);
    if (!flow.trim()) return;
    try {
      const r = await apiFetch<{ summary: string }>('/api/ai/summary', getToken, { method: 'POST', body: JSON.stringify({ flow, event: round.format.event }) });
      const { restore } = redactor(round, names);
      await run(uc.saveRoundSummary(roundId, restore(r.summary), 'claude'));
    } catch (e) {
      if (!opts.quiet) notifications.show({ color: 'yellow', title: 'No summary this time', message: createElement(Copy, null, message(e)) });
    }
  }, [deps, getToken, uc]);
};
